/**
 * Home Page Component
 *
 * Main landing page for the application.
 */
import { useNavigate } from 'react-router-dom'
import MeetupList from '@/features/meetups/meetup-list'
import useTopBGGGames from '@/hooks/useTopBGGGames'

function HomePage() {
  const navigate = useNavigate()
  const { games, isLoading, error, refreshGames } = useTopBGGGames(5, 100)

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <section className="py-8 px-4 text-center bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl border border-border">
        <h1 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">
          Welcome to Boardgames Today
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
          Discover, schedule, and join board game events in your area. Connect with fellow board
          game enthusiasts and never miss a game night again!
        </p>
        <div className="flex justify-center">
          <button
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-lg font-medium transition-colors"
            onClick={() => navigate('/meetups')}
          >
            Find Meetups
          </button>
        </div>
      </section>

      {/* Upcoming Meetups Section */}
      <section className="py-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Upcoming Meetups</h2>
          <button
            className="text-primary hover:text-primary/90 font-medium transition-colors"
            onClick={() => navigate('/meetups')}
          >
            View All
          </button>
        </div>
        <MeetupList limit={3} filter="all" />
      </section>

      {/* Popular Games Section */}
      <section className="py-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Popular Games</h2>
          <button
            className="text-primary hover:text-primary/90 font-medium transition-colors"
            onClick={refreshGames}
          >
            Refresh Games
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {isLoading ? (
            // Loading state
            Array(5)
              .fill(0)
              .map((_, i) => (
                <div key={`loading-${i}`} className="animate-pulse">
                  <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2 border border-border"></div>
                  <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              ))
          ) : error ? (
            // Error state
            <div className="col-span-full text-center text-error p-3 rounded-lg border border-error/20 bg-error/10">
              Failed to load popular games: {error}
            </div>
          ) : (
            // Successful load
            games.map(game => (
              <div
                key={game.id}
                className="group cursor-pointer"
                onClick={() =>
                  window.open(
                    `https://boardgamegeek.com/boardgame/${game.id}`,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                title={`View ${game.name} on BoardGameGeek`}
              >
                <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2 border border-border group-hover:border-primary/50 transition-colors">
                  {game.thumbnail ? (
                    <img
                      src={game.image || game.thumbnail}
                      alt={game.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onLoad={() => {
                        console.log('Game image loaded:', {
                          name: game.name,
                          usedUrl: game.image || game.thumbnail,
                          availableImage: game.image,
                          availableThumbnail: game.thumbnail,
                        })
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/5 to-secondary/5"></div>
                  )}
                </div>
                <h3
                  className="text-base font-medium group-hover:text-primary transition-colors line-clamp-1"
                  title={game.name}
                >
                  {game.name}
                </h3>
                <div className="flex items-center mt-1">
                  {game.averageRating && !isNaN(Number(game.averageRating)) && (
                    <div className="flex">
                      {[...Array(5)].map((_, j) => {
                        const starValue = (j + 1) * 2 // Convert to 10-point scale
                        return (
                          <span
                            key={j}
                            className={`text-xs ${
                              starValue <= Number(game.averageRating)
                                ? 'text-accent'
                                : 'text-muted-foreground'
                            }`}
                          >
                            ★
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <span className="text-xs text-muted-foreground ml-2">#{game.bggRank} on BGG</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Community Section */}
      <section className="py-6 px-4 bg-card rounded-xl border border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3">Join Our Community</h2>
          <p className="text-base text-muted-foreground mb-4">
            Connect with thousands of board game enthusiasts, share your experiences, and discover
            new games to play with friends and family.
          </p>
          <button className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-lg font-medium transition-colors">
            Sign Up Now
          </button>
        </div>
      </section>
    </div>
  )
}

export default HomePage
